import { ComponentProps, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  LinearProgress,
  List,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  FormControlLabel,
  TextField,
  Typography,
  Badge,
  Alert,
  Checkbox,
} from '@mui/material';
import { Add, ArrowDownward, ArrowUpward, Close, Tune } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { OrderOptionsValues } from '@bandido/shared/enums';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { FilterSelectorMenu } from '@frontend/features/common/components/FilterSelectorMenu';
import { MyFiltersMenu } from '@frontend/features/common/components/MyFiltersMenu';
import { TablePagination } from '@frontend/features/common/components/Table/TablePagination';
import { ROWS_PER_PAGE_OPTIONS } from '@frontend/features/common/components/Table';
import { useRememberedScroll } from '@frontend/features/common/hooks/useRememberedScroll';
import { CaseMobileCard } from './CaseMobileCard';
import { CasesListDesktop } from './CasesListDesktop';
import { CaseStatisticsModal } from './CaseStatisticsModal';
import { useCaseBulkActions } from '../hooks/useCaseBulkActions';
import { CaseBulkActionsMobile } from './CaseBulkActionsMobile';
import { mobileCaseSortOptions } from '../mobileCaseSortOptions';

type Props = ComponentProps<typeof CasesListDesktop> & {
  isError?: boolean;
  isPreviousData?: boolean;
  onRetry?: () => void;
};

export const CasesListMobile = ({
  title,
  subtitle,
  pagination,
  filtersCount,
  filtersOptions,
  onSearchChange,
  sorting,
  onCreate,
  actionToggles,
  statisticsButton,
  isLoading,
  isRefreshing = false,
  items,
  totalCount,
  customColumns = [],
  isStatisticsModalOpen,
  onCloseStatistics,
  isError,
  isPreviousData = false,
  onRetry,
}: Props) => {
  const { t } = useTranslation();
  const { ability } = useAuth();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const scrollRef = useRememberedScroll(!isLoading && !isPreviousData && !isError);
  const search = filtersOptions?.searchParams?.get('search') ?? '';
  const {order} = sorting.options;
  const bulk = useCaseBulkActions({ items, pagination, sorting, filtersOptions, onSearchChange,
    isUnavailable: isLoading || isRefreshing || isPreviousData || Boolean(isError) });
  const selectionDisabled = bulk.isUnavailable || bulk.isBulkUpdating;

  return (
    <Box
      ref={scrollRef}
      sx={{
        height: '100%',
        overflowY: 'auto',
        minWidth: 0,
        overscrollBehavior: 'contain',
        '& .MuiIconButton-root, & .MuiButton-root': { minHeight: 44, minWidth: 44 },
        '& input, & .MuiSelect-select': { fontSize: 16 },
      }}
    >
      <Box p={2} sx={{ pb: bulk.selectedIds.length ? 14 : 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} mb={2}>
          <Box minWidth={0}>
            <Typography variant="h5" component="h1">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
          {ability?.can('manage', 'Cases') && (
            <Button variant="contained" startIcon={<Add />} onClick={() => onCreate?.()}>
              {t('common.new')}
            </Button>
          )}
        </Stack>
        <Box sx={{ mb: 1, '& .MuiTextField-root': { maxWidth: 'none' } }}>
          <SearchInput value={search} onSearch={bulk.handleSearchChange} placeholder={t('common.search')} />
        </Box>
        <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} mb={1}>
          <Button
            variant="outlined"
            startIcon={
              <Badge badgeContent={filtersCount || undefined} color="error">
                <Tune />
              </Badge>
            }
            onClick={() => setFiltersOpen(true)}
          >
            {t('common.filters')}
          </Button>
          {actionToggles?.map((toggle) => (
            <FormControlLabel
              key={toggle.key}
              sx={{ mx: 0 }}
              control={<Switch checked={toggle.isActive} onChange={toggle.onToggle} />}
              label={toggle.label}
            />
          ))}
        </Stack>
        <Stack direction="row" alignItems="center" gap={1} mb={2}>
          <TextField
            select
            fullWidth
            size="small"
            label={t('common.mobileList.sort')}
            value={sorting.options.sort}
            onChange={(event) => bulk.sortingWithClear!.handler({ ...sorting.options, sort: event.target.value })}
          >
            {mobileCaseSortOptions.map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label === 'id' ? 'ID' : t(`cases.list.table.${label}`)}
              </MenuItem>
            ))}
          </TextField>
          <IconButton
            aria-label={t(
              order === OrderOptionsValues.asc ? 'common.mobileList.ascending' : 'common.mobileList.descending',
            )}
            onClick={() =>
              bulk.sortingWithClear!.handler({
                ...sorting.options,
                order: order === OrderOptionsValues.asc ? OrderOptionsValues.desc : OrderOptionsValues.asc,
              })
            }
          >
            {order === OrderOptionsValues.asc ? <ArrowUpward /> : <ArrowDownward />}
          </IconButton>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} mb={1} flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            {totalCount.toLocaleString()} {t('common.results')}
          </Typography>
          {statisticsButton}
        </Stack>
        {!isLoading && items.length > 0 && <FormControlLabel sx={{ mx: 0, mb: 1 }}
          control={<Checkbox disabled={selectionDisabled} checked={bulk.selectedIds.length === items.length}
            indeterminate={bulk.selectedIds.length > 0 && bulk.selectedIds.length < items.length}
            sx={{ width: 44, height: 44 }}
            onChange={(_, checked) => bulk.setSelectedIds(checked ? items.map(item => item.id) : [])} />}
          label={t('cases.list.bulk.selectPage')} />}
        {isRefreshing && <LinearProgress aria-label={t('cases.list.refreshing')} />}
        {isError && (
          <Alert severity="error" action={<Button onClick={onRetry}>{t('common.refresh')}</Button>}>
            {t('cases.list.error')}
          </Alert>
        )}
        {isLoading ? (
          Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} variant="rounded" height={160} sx={{ mb: 1.5 }} />
          ))
        ) : (
          <List disablePadding sx={{ display: 'grid', gap: 1.5 }}>
            {items.map((item) => (
              <CaseMobileCard key={item.id} item={item} customColumns={customColumns}
                selection={{ checked: bulk.selectedIds.includes(item.id), disabled: selectionDisabled,
                  onChange: checked => bulk.setSelectedIds(
                    checked ? [...bulk.selectedIds, item.id] : bulk.selectedIds.filter(id => id !== item.id),
                  ) }} />
            ))}
          </List>
        )}
        {!isLoading && !isError && items.length === 0 && (
          <Typography py={4} textAlign="center" color="text.secondary">
            {t('common.noData')}
          </Typography>
        )}
        <Box sx={{ '& .MuiGrid-root': { gap: 1, flexWrap: 'wrap' }, '& .MuiSelect-select': { fontSize: 16 } }}>
          <TablePagination
            compact
            totalRows={totalCount}
            page={pagination.page}
            rowsPerPage={pagination.rowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onChangePage={bulk.paginationWithClear!.handlePagination}
            onChangeRowsPerPage={bulk.paginationWithClear!.handleRowsPerPage}
          />
        </Box>
      </Box>
      <Drawer
        anchor="bottom"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        PaperProps={{
          sx: {
            maxHeight: '90dvh',
            borderRadius: '16px 16px 0 0',
            pb: 'env(safe-area-inset-bottom, 0px)',
            '& input, & .MuiSelect-select': { fontSize: 16 },
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
          <Typography variant="h6">{t('common.filters')}</Typography>
          <IconButton
            aria-label={t('common.close')}
            onClick={() => setFiltersOpen(false)}
            sx={{ minHeight: 44, minWidth: 44 }}
          >
            <Close />
          </IconButton>
        </Box>
        {filtersOptions?.filtersList && (
          <Stack gap={1} px={2} pb={2}>
            <MyFiltersMenu
              privateFilters={filtersOptions.filtersList.privateFilters}
              searchParams={filtersOptions.searchParams}
              setSearchParams={filtersOptions.setSearchParams}
            />
            <FilterSelectorMenu
              filtersList={filtersOptions.filtersList}
              searchParams={filtersOptions.searchParams}
              setSearchParams={filtersOptions.setSearchParams}
            />
          </Stack>
        )}
        {filtersOptions?.form}
        <Button sx={{ m: 2, minHeight: 44 }} variant="contained" onClick={() => setFiltersOpen(false)}>
          {t('common.mobileList.showResults')}
        </Button>
      </Drawer>
      <CaseBulkActionsMobile bulk={bulk} />
      <CaseStatisticsModal open={isStatisticsModalOpen} onClose={onCloseStatistics} />
    </Box>
  );
};
